import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, switchMap } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '../../../environments/environment';
import { SessionReloginService } from '../services/session/session-relogin.service';
import { ErrorDialogService } from '../services/error-dialog/error-dialog';

/**
 * Attaches the Microsoft ID token to API requests.
 * The backend validates the ID token (aud = our client ID) for authentication.
 * If silent token acquisition requires interaction (token fully expired),
 * triggers the session-expired flow immediately rather than waiting for 401.
 */
export const idTokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req)) {
    return next(req);
  }

  const msalService    = inject(MsalService);
  const sessionRelogin = inject(SessionReloginService);
  const errorDialog    = inject(ErrorDialogService);

  return from(getIdToken(msalService, sessionRelogin, errorDialog)).pipe(
    switchMap((idToken) => {
      const authReq = idToken
        ? req.clone({ setHeaders: { Authorization: `Bearer ${idToken}` } })
        : req;
      return next(authReq);
    })
  );
};

function isApiRequest(req: HttpRequest<unknown>): boolean {
  const url = req.urlWithParams ?? req.url;
  return url.startsWith(environment.API_URL);
}

async function getIdToken(
  msalService: MsalService,
  sessionRelogin: SessionReloginService,
  errorDialog: ErrorDialogService
): Promise<string | null> {
  try {
    const account = msalService.instance.getActiveAccount();
    if (!account) return null;

    const result = await msalService.instance.acquireTokenSilent({
      scopes: ['openid'],
      account
    });
    return result?.idToken ?? null;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Token can't be refreshed silently — user interaction required.
      // Show session-expired dialog proactively before any 401 is received.
      errorDialog.openSessionExpired(() => sessionRelogin.trigger());
    }
    return null;
  }
}
